import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Building, 
  Mail, 
  Phone, 
  CheckCircle2, 
  AlertCircle, 
  ShieldCheck, 
  BookOpen, 
  Search, 
  Sparkles,
  ArrowRight,
  Zap,
  Lock,
  Eye,
  EyeOff,
  Info
} from 'lucide-react';
import { authService, RegisterPayload } from '../../Services/AuthService';
import { useAuthModal } from '../../contexts/AuthModalContext';

export const RegisterView: React.FC = () => {
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();
  
  const [accountType, setAccountType] = useState<'INDIVIDUAL' | 'BUSINESS'>('INDIVIDUAL');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setErrorMessage('');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, ''); // Uniquement des chiffres
    if (val.startsWith('237') && val.length > 9) {
      val = val.slice(3);
    }
    val = val.slice(0, 9); // Bloqué strictement à 9 chiffres
    setFormData(prev => ({
      ...prev,
      phoneNumber: val
    }));
    setErrorMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Validations communes
    if (!formData.email || !formData.email.includes('@')) {
      setErrorMessage('Veuillez saisir une adresse email valide.');
      return;
    }

    const rawPhoneDigits = formData.phoneNumber.replace(/\D/g, '');
    const normalizedPhone = (rawPhoneDigits.startsWith('237') && rawPhoneDigits.length === 12) 
      ? rawPhoneDigits.slice(3) 
      : rawPhoneDigits;

    if (!normalizedPhone) {
      setErrorMessage('Veuillez renseigner votre numéro de téléphone.');
      return;
    }

    if (normalizedPhone.length !== 9) {
      setErrorMessage('Le numéro de téléphone doit comporter exactement 9 chiffres (ex: 698843185).');
      return;
    }

    // Mot de passe : minimum 4 caractères, aucune contrainte inutile
    if (!formData.password || formData.password.length < 4) {
      setErrorMessage('Le mot de passe doit comporter au moins 4 caractères.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Les deux mots de passe ne correspondent pas.');
      return;
    }

    // Validations par type de compte
    if (accountType === 'INDIVIDUAL') {
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setErrorMessage('Veuillez renseigner votre nom et prénom.');
        return;
      }
    } else {
      if (!formData.businessName.trim()) {
        setErrorMessage("Veuillez renseigner la raison sociale de votre entreprise.");
        return;
      }
    }

    setIsLoading(true);

    try {
      const payload: RegisterPayload = {
        accountType,
        email: formData.email.trim(),
        phoneNumber: normalizedPhone,
        password: formData.password,
        ...(accountType === 'INDIVIDUAL' ? {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim()
        } : {
          businessName: formData.businessName.trim()
        })
      };

      const response = await authService.register(payload);

      if (response.success) {
        setIsSuccess(true);
        setSuccessMessage(response.message || 'Votre inscription a bien été prise en compte.');
      } else {
        setErrorMessage(response.message || "Une erreur est survenue lors de l'inscription.");
      }
    } catch (err: any) {
      const serverMsg = err.message || err.data?.message || err.response?.data?.message || "Une erreur est survenue lors de la communication avec le serveur.";
      setErrorMessage(serverMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
      <div className="bg-white rounded-3xl shadow-xl border border-slate-200/80 overflow-hidden grid grid-cols-1 lg:grid-cols-12">
        
        {/* Left Hero Panel (Docarn Brand presentation) */}
        <div className="lg:col-span-5 bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 p-8 sm:p-10 text-white flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-violet-600/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-violet-200 text-xs font-semibold uppercase tracking-wider mb-6 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              Plateforme Juridique Professionnelle
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4 text-white leading-tight">
              Rejoignez le réseau Docarn Juridique.
            </h1>
            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed mb-8">
              Créez votre compte en quelques instants et bénéficiez de l'activation immédiate pour consulter les textes officiels et la jurisprudence.
            </p>

            {/* Features */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5 text-emerald-300">
                  <Zap className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-xs font-bold text-white uppercase tracking-wider">Activation Instantanée</h2>
                  <p className="text-[11px] text-slate-300 mt-0.5">Validez votre lien d'activation par email et connectez-vous immédiatement.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center shrink-0 mt-0.5 text-violet-300">
                  <BookOpen className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-xs font-bold text-white uppercase tracking-wider">Accès Immédiat</h2>
                  <p className="text-[11px] text-slate-300 mt-0.5">Accédez instantanément à la plateforme documentaire dès l'activation de votre compte.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center shrink-0 mt-0.5 text-violet-300">
                  <Search className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-xs font-bold text-white uppercase tracking-wider">Recherche Structurée & Rapide</h2>
                  <p className="text-[11px] text-slate-300 mt-0.5">Filtres par autorité émettrice, nature, statut juridique et mots-clés.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-10 pt-8 mt-8 border-t border-white/10 text-[11px] text-slate-400">
            DOCARN SERVICES SARL &bull; Solution de documentation juridique
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="lg:col-span-7 p-6 sm:p-10 flex flex-col justify-center">
          {isSuccess ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600 border border-emerald-100">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Vérifiez votre boîte e-mail</h3>
              <p className="text-sm text-slate-600 mb-6 leading-relaxed max-w-md mx-auto">
                {successMessage}
              </p>
              
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-left text-xs text-slate-600 space-y-2.5 mb-6 max-w-md mx-auto">
                <div className="flex items-center gap-2 text-violet-800 font-bold">
                  <ShieldCheck className="w-4 h-4 text-violet-700" />
                  <span>Activation instantanée :</span>
                </div>
                <ol className="list-decimal list-inside space-y-1.5 text-slate-600 pl-1">
                  <li>Cliquez sur le lien dans l'e-mail que nous venons de vous envoyer (valable 24h).</li>
                  <li>Votre compte est activé instantanément et vous accédez immédiatement à la plateforme.</li>
                  <li>
                    Connectez-vous avec votre identifiant : <strong>{accountType === 'INDIVIDUAL' ? formData.phoneNumber : formData.email}</strong>.
                  </li>
                </ol>
              </div>

              <div className="flex justify-center gap-3">
                <button 
                  onClick={() => navigate('/')}
                  className="py-2.5 px-6 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded-xl transition-colors cursor-pointer"
                >
                  Retour à l'accueil
                </button>
                <button 
                  onClick={openAuthModal}
                  className="py-2.5 px-6 bg-violet-700 hover:bg-violet-800 text-white font-semibold text-xs rounded-xl transition-colors cursor-pointer flex items-center gap-2"
                >
                  <span>Se connecter</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">Créer un compte</h3>
                  <p className="text-xs text-slate-500 mt-1">Activation immédiate dès vérification de votre email</p>
                </div>
                <button
                  type="button"
                  onClick={openAuthModal}
                  className="text-xs text-violet-700 hover:text-violet-900 font-semibold flex items-center gap-1 bg-violet-50 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                >
                  <span>Déjà membre ?</span>
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>

              {/* Account Type Selector */}
              <div className="grid grid-cols-2 gap-2 p-1.5 bg-slate-100 rounded-xl mb-5">
                <button
                  type="button"
                  onClick={() => { setAccountType('INDIVIDUAL'); setErrorMessage(''); }}
                  className={`flex items-center justify-center gap-2 py-2.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    accountType === 'INDIVIDUAL'
                      ? 'bg-white text-violet-800 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <User className="w-4 h-4" />
                  Particulier
                </button>
                <button
                  type="button"
                  onClick={() => { setAccountType('BUSINESS'); setErrorMessage(''); }}
                  className={`flex items-center justify-center gap-2 py-2.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    accountType === 'BUSINESS'
                      ? 'bg-white text-violet-800 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Building className="w-4 h-4" />
                  Entreprise / Cabinet
                </button>
              </div>

              {/* Error Notification */}
              {errorMessage && (
                <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5 text-xs text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Informative Notice about Connection Identifier */}
              <div className="mb-4 p-3 bg-indigo-50/80 border border-indigo-100 rounded-xl flex items-start gap-2.5 text-xs text-indigo-900">
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-indigo-600" />
                <span>
                  {accountType === 'INDIVIDUAL' ? (
                    <>
                      <strong>Identifiant de connexion :</strong> Votre <strong>numéro de téléphone</strong> vous servira d'identifiant pour vous connecter à votre compte.
                    </>
                  ) : (
                    <>
                      <strong>Identifiant de connexion :</strong> Votre <strong>adresse email</strong> vous servira d'identifiant pour vous connecter à votre compte.
                    </>
                  )}
                </span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3.5">
                {accountType === 'INDIVIDUAL' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                        Nom *
                      </label>
                      <div className="relative">
                        <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Ex: Kamga"
                          className="w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-slate-800 placeholder:text-slate-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                        Prénom *
                      </label>
                      <div className="relative">
                        <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Ex: Paul Arnaud"
                          className="w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-slate-800 placeholder:text-slate-400"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Raison Sociale / Nom du Cabinet *
                    </label>
                    <div className="relative">
                      <Building className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        name="businessName"
                        required
                        value={formData.businessName}
                        onChange={handleChange}
                        placeholder="Ex: DOCARN SERVICES SARL"
                        className="w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-slate-800 placeholder:text-slate-400"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Téléphone *
                      </label>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {formData.phoneNumber.length}/9 chiffres
                      </span>
                    </div>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={9}
                        name="phoneNumber"
                        required
                        value={formData.phoneNumber}
                        onChange={handlePhoneChange}
                        placeholder="Ex: 698843185"
                        className="w-full pl-10 pr-4 py-2.5 text-xs font-mono tracking-wide bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-slate-800 placeholder:text-slate-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Adresse Email *
                    </label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={accountType === 'BUSINESS' ? "Ex: contact@docarn.cm" : "Ex: paul.kamga@docarn.cm"}
                        className="w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-slate-800 placeholder:text-slate-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Password & Confirm Password */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Mot de passe * <span className="text-[10px] lowercase text-slate-400 font-normal">(min 4 car.)</span>
                    </label>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        required
                        minLength={4}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Votre mot de passe"
                        className="w-full pl-10 pr-10 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-slate-800 placeholder:text-slate-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer p-0.5"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      Confirmer le mot de passe *
                    </label>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        required
                        minLength={4}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Répétez le mot de passe"
                        className="w-full pl-10 pr-10 py-2.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all text-slate-800 placeholder:text-slate-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer p-0.5"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full mt-4 py-3 px-6 bg-violet-700 hover:bg-violet-800 active:bg-violet-900 text-white font-bold text-xs rounded-xl shadow-sm transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Inscription en cours...</span>
                    </>
                  ) : (
                    <>
                      <span>Créer mon compte & activer l'accès</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
